'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { Button, Padding, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { validateShopTitle } from '@musinsa/services-curator';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { getBrandConfig } from '../../common';
import { PageViewTracker } from '../../common/components';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { FunnelProvider, useFunnelContext } from '../context/FunnelContext';
import { AccountFunnel } from '../funnels/AccountFunnel';
import { SNSFunnel } from '../funnels/SNSFunnel';
import { useJoinFunnel } from '../hooks/useJoinFunnel';
import { useSubmitJoinApplication } from '../hooks/useSubmitJoinApplication';
import { FUNNEL_STEPS, type FunnelStep } from '../types';

interface BackPressResult {
  handled: boolean;
  currentStep: FunnelStep;
}

interface Props {
  className?: string;
  onBackPress?: (handleBackPress: () => BackPressResult) => void;
}

const shopTitleUnValidatedErrorMessage = '큐레이터샵 타이틀은 영문과 숫자만 사용 가능합니다';

const noopButtonStateChange = () => undefined;

const JoinFunnelContent = ({ className, onBackPress }: Props) => {
  const { step, formValues, onNextStep, canProceedToNext, onPrevStep } = useFunnelContext();
  const { mutate, error, reset, isPending: submitPending } = useSubmitJoinApplication();
  const [clientValidationError, setClientValidationError] = useState<string>('');
  const { service } = useDeviceInfo();
  const brandConfig = getBrandConfig(service);

  const handleBackPress = useCallback((): BackPressResult => {
    if (step > FUNNEL_STEPS.SNS) {
      onPrevStep();
      return { handled: true, currentStep: step };
    }
    return { handled: false, currentStep: step };
  }, [step, onPrevStep]);

  const handleAccountSubmit = useCallback(() => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_signup',
          section_name: 'AccountFunnel',
          section_title: '계정 정보 입력',
          position: -1,
          button_id: 'submit_signup',
          button_name: '큐레이터 가입 신청하기',
          stage_status: 'step3_success',
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        event_name: 'click_button',
        from: getScreenName(),
        section_name: 'curator_fab',
        button_name: 'curator_start',
      });
      pushDataLayer('click_button', {
        section_name: 'curator_fab',
        section_index: '1',
        button_name: 'curator_start',
        index: GTM_NOTSET_STRING,
        brand_id: GTM_NOTSET_STRING,
      });
    }
    const validationResult = validateShopTitle(formValues.shopTitle);

    if (!validationResult.isValid) {
      setClientValidationError(shopTitleUnValidatedErrorMessage);
      return;
    }

    setClientValidationError('');

    const finalFormValues = {
      ...formValues,
      agreedTerms: [...brandConfig.agreedTerms],
    };
    mutate(finalFormValues);
  }, [mutate, formValues, brandConfig]);

  const handleClickNext = useCallback(() => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_signup',
          section_name: 'SNSFunnel',
          section_title: 'SNS 정보 입력',
          position: -1,
          button_id: 'next_step',
          button_name: '다음',
          stage_status: 'step2_success',
        },
      });
    }
    onNextStep();
  }, [onNextStep, service]);

  const clearAllErrors = useCallback(() => {
    setClientValidationError('');
    reset();
  }, [reset]);

  useEffect(() => {
    if (onBackPress) {
      onBackPress(handleBackPress);
    }
  }, [onBackPress, handleBackPress]);

  const renderContent = () => {
    switch (step) {
      case FUNNEL_STEPS.SNS:
        return <SNSFunnel />;
      case FUNNEL_STEPS.ACCOUNT:
        return (
          <AccountFunnel
            onButtonStateChange={noopButtonStateChange}
            submitError={error?.data?.meta?.message || clientValidationError}
            onClearError={clearAllErrors}
          />
        );
      default:
        return null;
    }
  };

  const renderBottomButton = () => {
    switch (step) {
      case FUNNEL_STEPS.SNS:
        return (
          <Button className="w-full" disabled={!canProceedToNext()} onClick={handleClickNext}>
            <Padding asChild x="4" y="3">
              <Typography size="14" weight="medium">
                다음
              </Typography>
            </Padding>
          </Button>
        );
      case FUNNEL_STEPS.ACCOUNT:
        return (
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={!canProceedToNext() || submitPending}
              onClick={handleAccountSubmit}
            >
              <Padding asChild x="4" y="3">
                <Typography size="14" weight="medium">
                  신청하기
                </Typography>
              </Padding>
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const shouldTrackMusinsaPageView = service === SERVICE.MUSINSA;

  return (
    <div className={twMerge('w-full bg-white flex flex-col grow', className)}>
      {shouldTrackMusinsaPageView && (
        <PageViewTracker pageName="curator_signup" musinsaPageName="screen_curator_apply_join" />
      )}
      {renderContent()}
      <BottomButtonWrapper>
        <div className="py-3">{renderBottomButton()}</div>
      </BottomButtonWrapper>
    </div>
  );
};

export const JoinContainer = (props: Props) => {
  const funnelState = useJoinFunnel();

  return (
    <FunnelProvider value={funnelState}>
      <JoinFunnelContent {...props} />
    </FunnelProvider>
  );
};
